import React from 'react';
import { useTranslation } from 'react-i18next';
import { MessageSquare, Filter, Award, ChevronRight, ShieldCheck } from 'lucide-react';

const CredibilityDiscussionFeed = ({
  posts,
  activeStock,
  feedFilter,
  onFeedFilterChange,
}) => {
  const { t } = useTranslation();
  return (
    <div className="bg-cream-100 dark:bg-slate-900/80 border border-cream-300 dark:border-slate-700/60 rounded-xl overflow-hidden shadow-xl shadow-cream-900/5 dark:shadow-slate-900/60 relative">
      <div className="absolute left-0 top-0 bottom-0 w-1 bg-gradient-to-b from-blue-500 via-purple-500 to-blue-500" />

      {/* HEADER */}
      <div className="p-5 border-b border-cream-300 dark:border-slate-800/60 bg-cream-200 dark:bg-slate-900/40">
        <div className="flex items-center justify-between mb-3">
          <div className="flex flex-col gap-1.5">
            <div className="flex items-center gap-2">
              <MessageSquare className="w-5 h-5 text-purple-600 dark:text-purple-400" />
              <h2 className="text-base font-bold text-gray-900 dark:text-slate-200">
                {t('dashboard.credibilityDiscussionFeed.title')}
              </h2>
              <span className="text-xs px-2 py-0.5 bg-purple-100 dark:bg-purple-500/10 text-purple-700 dark:text-purple-300 rounded border border-purple-500/20 font-medium">
                {t('dashboard.credibilityDiscussionFeed.live')}
              </span>
            </div>

            <p className="text-xs text-gray-500 dark:text-slate-500 leading-relaxed">
              {t('dashboard.credibilityDiscussionFeed.subtitle')}
            </p>

            {activeStock && (
              <p className="text-[11px] text-gray-500 dark:text-slate-500">
                {t('dashboard.credibilityDiscussionFeed.showingCommentaryFocusedOn')}{' '}
                <span className="font-semibold text-gray-900 dark:text-slate-200">
                  {activeStock}
                </span>.
                {' '}{t('dashboard.credibilityDiscussionFeed.changeFocusHint')}
              </p>
            )}
          </div>
        </div>

        {/* FILTER BAR */}
        <div className="flex items-center gap-2">
          <select
            value={feedFilter}
            onChange={(e) => onFeedFilterChange(e.target.value)}
            aria-label={t('dashboard.credibilityDiscussionFeed.filterAriaLabel')}
            className="px-3 py-2 bg-white dark:bg-slate-800/60 border border-gray-300 dark:border-slate-700/60 rounded-lg text-sm text-gray-700 dark:text-slate-300 focus:outline-none focus:border-gray-400 dark:focus:border-slate-600 cursor-pointer"
          >
            <option value="watchlist">{t('dashboard.credibilityDiscussionFeed.filterWatchlist')}</option>
            <option value="all">{t('dashboard.credibilityDiscussionFeed.filterAllMarket')}</option>
            <option value="groups">{t('dashboard.credibilityDiscussionFeed.filterMyGroups')}</option>
            <option value="following">{t('dashboard.credibilityDiscussionFeed.filterFollowing')}</option>
          </select>

          <button className="px-3 py-2 bg-gray-100 hover:bg-gray-200 dark:bg-slate-800/40 dark:hover:bg-slate-800/60 border border-gray-300 dark:border-slate-700/60 rounded-lg text-sm text-gray-500 hover:text-gray-700 dark:text-slate-400 dark:hover:text-slate-300 transition-colors flex items-center gap-2">
            <Filter className="w-4 h-4" />
            {t('dashboard.credibilityDiscussionFeed.advancedFilters')}
          </button>
        </div>
      </div>

      {/* FEED */}
      {posts.length === 0 ? (
        <div className="p-8 text-center">
          <MessageSquare className="w-12 h-12 text-gray-400 dark:text-slate-600 mx-auto mb-3 opacity-50" />
          <p className="text-gray-500 dark:text-slate-400 text-sm">
            {t('dashboard.credibilityDiscussionFeed.noDiscussionsYet')}
          </p>
        </div>
      ) : (
      <div className="divide-y divide-cream-300 dark:divide-slate-800/40">
        {posts.map((post) => {
          const initials = post.user.username
            .split(' ')
            .map((n) => n[0])
            .join('');

          const credibilityTier =
            post.user.credibility_score >= 90
              ? t('dashboard.credibilityDiscussionFeed.highAlignment')
              : post.user.credibility_score >= 70
              ? t('dashboard.credibilityDiscussionFeed.moderateAlignment')
              : t('dashboard.credibilityDiscussionFeed.limitedAlignment');

          return (
            <div
              key={post.id}
              className="p-5 hover:bg-cream-900/5 dark:hover:bg-slate-800/30 transition-colors cursor-pointer group"
            >
              <div className="flex gap-4">
                {/* AVATAR */}
                <div
                  className={`w-12 h-12 bg-gradient-to-br ${post.user.avatar_color} rounded-full flex items-center justify-center text-sm font-bold flex-shrink-0 shadow-lg ring-2 ring-white dark:ring-slate-900`}
                >
                  {initials}
                </div>

                <div className="flex-1 min-w-0">
                  {/* USER META */}
                  <div className="flex items-center gap-2 mb-2 flex-wrap">
                    <span className="font-semibold text-gray-900 dark:text-slate-100 hover:text-blue-600 dark:hover:text-blue-400 transition-colors">
                      {post.user.username}
                    </span>

                    <span className="text-xs px-2.5 py-0.5 bg-blue-500/15 text-blue-700 dark:text-blue-300 rounded-full border border-blue-500/25 font-bold flex items-center gap-1">
                      <ShieldCheck className="w-3 h-3" />
                      {t('dashboard.credibilityDiscussionFeed.histScore', { score: post.user.credibility_score })}
                    </span>

                    <span className="text-[11px] text-gray-500 dark:text-slate-500">
                      {credibilityTier}
                    </span>

                    <span className="text-xs text-gray-500 dark:text-slate-500">
                      {post.created_at}
                    </span>

                    {post.user.credibility_score >= 90 && (
                      <span className="text-xs px-2 py-0.5 bg-amber-100 dark:bg-amber-500/10 text-amber-700 dark:text-amber-300 rounded border border-amber-500/20 font-medium">
                        {t('dashboard.credibilityDiscussionFeed.frequentlyActiveMember')}
                      </span>
                    )}
                  </div>

                  {/* POST CONTENT */}
                  <p className="text-sm text-gray-800 dark:text-slate-200 leading-relaxed mb-3">
                    {post.content}
                  </p>

                  {/* TICKERS + STATS */}
                  <div className="flex items-center gap-3 flex-wrap">
                    {post.tickers.map((ticker) => (
                      <span
                        key={ticker}
                        className={`text-xs px-2.5 py-1 rounded-md font-semibold border ${
                          ticker === activeStock
                            ? 'bg-violet-500/20 text-violet-700 dark:text-violet-200 border-violet-500/60'
                            : 'bg-gray-100 dark:bg-slate-800/60 text-blue-700 dark:text-blue-300 border-gray-300 dark:border-slate-700/50 hover:border-blue-500/50'
                        } transition-colors`}
                      >
                        ${ticker}
                      </span>
                    ))}

                    <span className="text-xs text-gray-500 dark:text-slate-500 flex items-center gap-1.5 ml-auto">
                      <MessageSquare className="w-3.5 h-3.5" />
                      {post.comments}
                    </span>

                    <span className="text-xs text-gray-500 dark:text-slate-500 flex items-center gap-1.5">
                      <Award className="w-3.5 h-3.5" />
                      {post.reactions}
                    </span>
                  </div>

                </div>
              </div>
            </div>
          );
        })}
      </div>
      )}

      {/* FOOTER -- hidden when the feed is empty, since "Load More" makes
          no sense with nothing loaded. */}
      {posts.length > 0 && (
        <div className="p-4 border-t border-cream-300 dark:border-slate-800/60 bg-cream-200 dark:bg-slate-900/40 text-center">
          <button className="text-sm text-blue-600 hover:text-blue-700 dark:text-blue-400 dark:hover:text-blue-300 font-medium flex items-center gap-2 mx-auto transition-colors">
            {t('dashboard.credibilityDiscussionFeed.loadMore')}
            <ChevronRight className="w-4 h-4" />
          </button>
        </div>
      )}
    </div>
  );
};

export default CredibilityDiscussionFeed;
